/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import {useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import isNull from 'lodash.isnull';
import moment from 'moment';
import {translate} from '@ion/core/src/locale/locale';
import {getUserTimeZoneOffset} from '@ion/core/src/timezone/timezone';
import {Device, GenericObject} from '@ion/core/src/types/entities';
import {
  getCurrentDate,
  timeDiff,
  toTimeZoneUtcOffset
} from '@ion/core/src/utils/DateUtil';
import {CSVExportButton} from '@ion/dls/src/components/buttons/CSVExportButton';
import {PrimaryButton} from '@ion/dls/src/components/buttons/PrimaryButton';
import {RefreshGridButton} from '@ion/dls/src/components/buttons/RefreshGridButton';
import DatePicker from '@ion/dls/src/editors/datetimepicker/DatePicker';
import {IonSnackbar} from '@ion/dls/src/notifications/IonSnackbar';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {EventTypeChooser} from '@ion/features/src/entities/events/EventTypeChooser';
import {ExportContext} from '../../types/redux';
import EventsGrid from './EventsGrid';
import {
  adminDevicesViewEvents,
  listEventObj,
  listOfMsgType
} from './ViewDeviceEditorUtils';

export interface EventsTabProps {
  tabIndex: Number;
  tabName?: string;
  device?: Device;
};

const useStyles = makeStyles((theme: DLSTheme) => ({
  formSection : {
    ...dlsAlt('typography', 'cta3'),
    fontWeight    : 700,
    color         : dlsColor('grey1'),
    letterSpacing : '0',
    padding       : '20px 0 10px 0'
  },
  flexStyle : {
    display                        : 'flex',
    marginTop                      : '20px',
    alignItems                     : 'center',
    [theme.breakpoints.down('sm')] : {
      flexWrap : 'wrap'
    }
  },
  dropdownStyle : {
    width                          : '200px',
    marginRight                    : '10px',
    [theme.breakpoints.down('sm')] : {
      width       : '48%',
      marginRight : '5px'
    }
  },
  eventDropdownStyle : {
    width                          : '200px',
    [theme.breakpoints.down('sm')] : {
      width : '170px'
    }
  },
  searchContainer : {
    marginRight : '10px',
    marginTop   : '26px'
  },
  iconStyle : {
    display     : 'flex',
    marginLeft  : 'auto',
    marginRight : '10px',
    marginTop   : '25px'
  },
  headerItem : {
    ...dlsAlt('typography', 'label3'),
    fontWeight    : 700,
    color         : dlsColor('grey1'),
    padding       : '5px 5px 5px 10px',
    textTransform : 'capitalize'
  },
  bodyItem : {
    ...dlsAlt('typography', 'label2'),
    color : dlsColor('aaBlue')
  },
  buttonStyle : {
    marginTop : '25px'
  },
  buttonGroup : {
    height : '30px'
  },
  gridContainer : {
    margin : '20px 10px 20px 0',
    height : 'calc(100vh - 454px)'
  },
  detailsTab : {
    height : '100%'
  }
}));

/**
 * EventsTab component that displays the Device Events
 * data for the selected Device Details tab.
 */
export const EventsTab = ({
  device,
  tabName,
  tabIndex = 0
}: EventsTabProps) => {
  const allLabel   = translate('common:features.eventType.allLabel');
  const dateFormat = 'YYYY-MM-DDTHH:mm:ss.000[Z]';

  const classes                             = useStyles();
  const gridRef                             = useRef<GenericObject>();
  const {pending}                           = useSelector(({exporter}) => exporter.current);
  const isExporting                         = pending[ExportContext.eventHistory] === true;
  const [startDate, setStartDate]           = useState<Date | null>(null);
  const [endDate, setEndDate]               = useState<Date | null>(null);
  const [eventTime, setEventTime]           = useState<string | null>(null);
  const [isValidDates, setIsValidDates]     = useState<boolean>(true);
  const [showErrorSnack, setShowErrorSnack] = useState<boolean>(false);
  const [eventType, setEventType]           = useState<string | null>(null);
  const [refreshGrid, setRefreshGrid]       = useState(false);
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [totalRowsCount, setTotalRowsCount] = useState<number>(0);

  const onEventTypeChange = useCallback(function(value) {
    const eventName = value?.name || '';
    if (eventName) {
      const listEventsArray = Object.keys(listEventObj);
      let selEvent          = listEventsArray.indexOf(eventName) > -1 ? listEventObj[eventName] : eventName;
      selEvent              = selEvent?.replace(/ /g, '');
      setEventType(listOfMsgType[selEvent]);
    } else {
      setEventType(null);
    }
  }, [listEventObj]);

  const toggleDatePicker = useCallback(function() {
    setOpenDatePicker(true);
    setTimeout(() => {
      setOpenDatePicker(false);
    }, 1000);
  }, []);

  const onExport = useCallback(function() {
    gridRef.current?.onExport();
  }, []);

  const refreshData = useCallback(function() {
    setRefreshGrid(true);
    setTimeout(() => {
      setRefreshGrid(false);
    }, 500);
  }, [device]);

  useEffect(() => {
    if (!isNull(startDate) && !isNull(endDate)) {
      const daysDiff           = timeDiff(endDate as Date, startDate as Date);
      const isValid            = daysDiff >= 0 && daysDiff <= 7;
      const userTimeZoneOffset = getUserTimeZoneOffset(); // in minutes

      setIsValidDates(isValid);
      
      if (isValid) {
        // Interpret the picked dates as local calendar days, then convert to 
        // UTC-offset string for the query.
        const startDateTime = moment.tz(startDate, moment.tz.guess()).startOf('day').format(dateFormat);
        const endDateTime   = moment.tz(endDate, moment.tz.guess()).endOf('day').format(dateFormat);

        const userTimeZoneStartDateTime = toTimeZoneUtcOffset(startDateTime, -userTimeZoneOffset, dateFormat);
        const userTimeZoneEndDateTime   = toTimeZoneUtcOffset(endDateTime, -userTimeZoneOffset, dateFormat);
        const range                     = `[${userTimeZoneStartDateTime} TO ${userTimeZoneEndDateTime}]`;

        setEventTime(range);
      }
    }
  }, [startDate, endDate]);

  const OnChangeStartDate = useCallback((value: Date | null) => {
    setStartDate(value);
  }, []);

  const OnChangeEndDate = useCallback((value: Date | null) => {
    setEndDate(value);
  }, []);

  useEffect(() => {
    refreshData();
  }, [eventType, device]);

  const onSearch = useCallback(function() {
    if (!isValidDates) {
      setShowErrorSnack(true);
    } else {
      refreshData();
    }
  }, [isValidDates, eventTime, refreshData]);

  const onCloseSnack = useCallback(() => {
    setShowErrorSnack(false);
  }, []);

  return (
    <div id={`detailsTab_${tabIndex}`} className={classes.detailsTab}>
      <div className={classes.flexStyle}>
        <div className={classes.dropdownStyle}>
          <DatePicker closeOnClickOutside
            useDatePickerTextField
            disableFuture
            hideClearButton
            open={openDatePicker}
            fieldLabel={translate(`${adminDevicesViewEvents}.startDate`)}
            hasError={showErrorSnack}
            toggle={toggleDatePicker}
            defaultValue={getCurrentDate()}
            onChange={OnChangeStartDate} />
        </div>
        <div className={classes.dropdownStyle}>
          <DatePicker closeOnClickOutside
            useDatePickerTextField
            disableFuture
            hideClearButton
            open={openDatePicker}
            fieldLabel={translate(`${adminDevicesViewEvents}.endDate`)}
            hasError={showErrorSnack}
            toggle={toggleDatePicker}
            defaultValue={getCurrentDate()}
            onChange={OnChangeEndDate} />
        </div>
        <div className={classes.searchContainer}>
          <PrimaryButton onClick={onSearch}>
            {translate(`${adminDevicesViewEvents}.searchButton`)}
          </PrimaryButton>
        </div>
        <div className={classnames(classes.eventDropdownStyle, classes.searchContainer)}>
          <EventTypeChooser includeNone
            noneOptionValue='all'
            noneOptionLabel={allLabel}
            displayLabel={false}
            deviceId={device?.id!}
            defaultValue={eventType || allLabel}
            onChange={onEventTypeChange} />
        </div>
        <div className={classes.iconStyle}>
          <CSVExportButton onExport={onExport}
            isExporting={isExporting}
            enableExport={totalRowsCount === 0}
            hasNoRowSelection={totalRowsCount === 0}
            disableExport />
          <RefreshGridButton onRefresh={refreshData} />
        </div>
      </div>
      <div className={classes.gridContainer}>
        <EventsGrid device={device}
          ref={gridRef}
          refreshGrid={refreshGrid}
          tabName={tabName}
          setTotalRowsCount={setTotalRowsCount}
          eventTime={eventTime}
          eventType={eventType!} />
      </div>

      {showErrorSnack && (
        <IonSnackbar show
          customDismissLabel={translate(`${adminDevicesViewEvents}.dismiss`)}
          message={translate(`${adminDevicesViewEvents}.dateError`)}
          onDismiss={onCloseSnack} />
      )}
    </div>
  );
};

export default EventsTab;
